import { useState, useCallback, useEffect, useRef } from 'react';
import { PresenceNodeData } from '@/types/presence';

interface UsePresenceStateProps {
  id: string;
  data: any;
  updateNodeData: (id: string, updaterFn: (prevData: any) => any) => void;
}

export const usePresenceState = ({ id, data, updateNodeData }: UsePresenceStateProps) => {
  // Node state
  const [isLocked, setIsLocked] = useState<boolean>(data.isLocked || false);
  const [isMinimized, setIsMinimized] = useState<boolean>(data.isMinimized || false);
  const [minimizeLevel, setMinimizeLevel] = useState<'normal' | 'medium' | 'minimum'>(data.minimizeLevel || 'normal');
  const [activeTab, setActiveTab] = useState<'users' | 'chat' | 'timeline'>('users');
  const [assignedEmails, setAssignedEmails] = useState<string[]>(
    Array.isArray(data.assignedEmails) ? data.assignedEmails : (data.assignedEmails ? data.assignedEmails.split(' ') : [])
  );
  const [activeUsers, setActiveUsers] = useState<PresenceNodeData['activeUsers']>(
    data.activeUsers || {}
  );
  const [chatMessages, setChatMessages] = useState<PresenceNodeData['chatMessages']>(
    data.chatMessages || []
  );
  
  // Track last synced data to avoid unnecessary updates
  const lastSyncedData = useRef<PresenceNodeData>({
    isLocked: data.isLocked || false,
    isMinimized: data.isMinimized || false,
    minimizeLevel: data.minimizeLevel || 'normal',
    label: data.label || 'Project Presence',
    assignedEmails: data.assignedEmails || [],
    activeUsers: data.activeUsers || {},
    chatMessages: data.chatMessages || [],
    activeTab: data.activeTab || 'users',
    systemState: data.systemState || { lastCheck: Date.now(), isHealthy: true },
  });

  // Update state from data prop changes
  useEffect(() => {
    if (data.isLocked !== undefined) setIsLocked(data.isLocked);
    if (data.isMinimized !== undefined) setIsMinimized(data.isMinimized);
    if (data.minimizeLevel !== undefined) {
      setMinimizeLevel(data.minimizeLevel);
      setIsMinimized(data.minimizeLevel !== 'normal');
    }
    if (data.assignedEmails !== undefined) {
      const emails = Array.isArray(data.assignedEmails) ? 
        data.assignedEmails : 
        (data.assignedEmails ? data.assignedEmails.split(' ') : []);
      setAssignedEmails(emails);
    }
    if (data.activeUsers !== undefined) setActiveUsers(data.activeUsers);
    if (data.chatMessages !== undefined) setChatMessages(data.chatMessages);
  }, [data]);

  // Helper function to sync node data with the graph - following QuickStartState pattern
  const syncNodeData = useCallback((newData: Partial<PresenceNodeData>) => {
    updateNodeData(id, (prevData) => {
      const cleanPrevData = { ...prevData };
      // Prevent recursive nesting
      if ('data' in cleanPrevData && typeof cleanPrevData.data === 'object' && cleanPrevData.data !== null) {
        delete cleanPrevData.data;
      }
      const updatedData = { ...cleanPrevData, ...newData };
      lastSyncedData.current = { ...lastSyncedData.current, ...newData };
      return updatedData;
    });
  }, [id, updateNodeData]);

  // Event handlers
  const handleLockChange = useCallback((locked: boolean) => {
    setIsLocked(locked);
    syncNodeData({ isLocked: locked });
  }, [syncNodeData]);

  const handleMinimizeLevelChange = useCallback((level: 'normal' | 'medium' | 'minimum') => {
    setMinimizeLevel(level);
    setIsMinimized(level !== 'normal');
    syncNodeData({ 
      minimizeLevel: level,
      isMinimized: level !== 'normal'
    });
  }, [syncNodeData]);

  const handleMinimizeChange = useCallback((minimized: boolean) => {
    setIsMinimized(minimized);
    const level = minimized ? 'medium' : 'normal';
    setMinimizeLevel(level);
    syncNodeData({ 
      isMinimized: minimized,
      minimizeLevel: level
    });
  }, [syncNodeData]);

  const handleTitleChange = useCallback((title: string) => {
    syncNodeData({ label: title });
  }, [syncNodeData]);

  const handleAssignUsersClick = useCallback(() => {
    const emails = window.prompt(
      'Enter emails separated by space',
      assignedEmails.join(' ')
    );
    if (emails !== null) {
      const emailArray = emails.split(' ').filter(e => e.trim());
      setAssignedEmails(emailArray);
      syncNodeData({ assignedEmails: emailArray });
    }
  }, [assignedEmails, syncNodeData]);

  const handleTabChange = useCallback((tab: 'users' | 'chat' | 'timeline') => {
    setActiveTab(tab);
  }, []);

  // Update user presence data
  const updateUserPresence = useCallback((email: string, nodeId: string | null, status = 'viewing') => {
    const updatedActiveUsers = { ...activeUsers };
    
    if (nodeId) {
      // Add or update user
      updatedActiveUsers[email] = {
        email,
        currentNodeId: nodeId,
        lastSeen: Date.now(),
        status
      };
    } else {
      // Remove user if nodeId is null
      if (updatedActiveUsers[email]) {
        delete updatedActiveUsers[email];
      }
    }
    
    setActiveUsers(updatedActiveUsers);
    syncNodeData({ activeUsers: updatedActiveUsers });
  }, [activeUsers, syncNodeData]);

  // Add a new chat message
  const addChatMessage = useCallback((content: string, email?: string, timestamp?: number) => {
    const newMessage = {
      id: `msg-${timestamp || Date.now()}`,
      content,
      email: email || 'current-user@example.com',
      timestamp: timestamp || Date.now()
    };
    
    const updatedMessages = [...chatMessages, newMessage];
    setChatMessages(updatedMessages);
    syncNodeData({ chatMessages: updatedMessages });
  }, [chatMessages, syncNodeData]);

  return {
    // State
    isLocked,
    isMinimized,
    minimizeLevel,
    activeTab,
    assignedEmails,
    activeUsers,
    chatMessages,
    
    // Handlers
    handleLockChange,
    handleMinimizeChange,
    handleMinimizeLevelChange,
    handleTitleChange,
    handleAssignUsersClick,
    handleTabChange,
    updateUserPresence,
    addChatMessage,
    syncNodeData
  };
};

export type { PresenceNodeData };